import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronRight, RefreshCw, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  listCatalogVersionAdoptionsQueryKey,
  revertCatalogVersionAdoptionMutation,
  upgradeCatalogVersionAdoptionMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { Can } from '../auth/Can.js';
import { Badge, Button, sentenceCase } from '../ui';
import type { CatalogAdoptionStatus } from '../../services/api';

interface CatalogAdoptionStatusPanelProps {
  adoptions: CatalogAdoptionStatus[];
  loading?: boolean;
  title?: string;
  compact?: boolean;
  maxVisible?: number;
  onChanged?: () => void;
}

export function CatalogAdoptionStatusPanel({
  adoptions,
  loading = false,
  title = 'Library version updates',
  compact = false,
  maxVisible = 3,
  onChanged,
}: CatalogAdoptionStatusPanelProps) {
  const queryClient = useQueryClient();
  const stale = adoptions.filter((adoption) => adoption.status === 'stale' || adoption.status === 'adoptable');
  const visible = stale.slice(0, maxVisible);
  const hiddenCount = Math.max(0, stale.length - visible.length);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: listCatalogVersionAdoptionsQueryKey() });
    onChanged?.();
  };

  const upgrade = useMutation({ ...upgradeCatalogVersionAdoptionMutation(), onSuccess: refresh });
  const revert = useMutation({ ...revertCatalogVersionAdoptionMutation(), onSuccess: refresh });

  if (loading) {
    return compact ? null : (
      <div className="rounded-lg border bg-surface p-3 text-sm text-muted">Checking adopted library versions...</div>
    );
  }
  if (stale.length === 0) return null;

  return (
    <div className="rounded-lg border border-warning/30 bg-warning-muted/40 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <AlertTriangle size={compact ? 14 : 16} className="text-warning" />
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={compact ? 'text-xs font-medium text-heading' : 'text-sm font-semibold text-heading'}>
                {title}
              </span>
              <Badge variant="warning">{stale.length} actionable</Badge>
            </div>
            {!compact && (
              <p className="mt-1 text-xs text-muted">
                These records consume older promoted library versions. Upgrade only after reviewing downstream impact.
              </p>
            )}
          </div>
        </div>
        <Link
          to="/agents/libraries/catalog-review?tab=adoptions"
          className="inline-flex items-center gap-1 text-xs font-medium text-warning hover:underline"
        >
          Open adoptions <ChevronRight size={13} />
        </Link>
      </div>

      <div className={compact ? 'mt-2 space-y-2' : 'mt-3 space-y-2'}>
        {visible.map((adoption) => (
          <div
            key={adoption.consumerKey}
            className="flex flex-col gap-2 rounded-sm border border-warning/20 bg-surface/80 p-2 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <div className="truncate text-xs font-medium text-heading">{adoption.label}</div>
              <div className="mt-0.5 flex flex-wrap gap-2 text-[11px] text-muted">
                <span>{formatCatalogAdoptionLabel(adoption.consumerType)}</span>
                <span>{sentenceCase(adoption.status)}</span>
                <span>{adoption.adoptedVersion == null ? 'unpinned' : `adopted v${adoption.adoptedVersion}`}</span>
                <span>latest v{adoption.latestVersion ?? 'n/a'}</span>
                {adoption.proposalInterventionId && <span>Proposal open</span>}
              </div>
            </div>
            <Can policy="RequireSuperadmin">
              <div className="flex shrink-0 flex-wrap items-center gap-1">
                <Button
                  size="sm"
                  variant="secondary"
                  className="bg-warning-muted text-warning hover:bg-warning-muted/80"
                  disabled={!adoption.canUpgrade}
                  loading={upgrade.isPending && upgrade.variables?.body?.consumerKey === adoption.consumerKey}
                  onClick={() => upgrade.mutate({ body: { consumerKey: adoption.consumerKey } })}
                >
                  <RefreshCw size={12} />{' '}
                  {adoption.proposalRequired ? 'Proposed' : adoption.status === 'adoptable' ? 'Pin' : 'Upgrade'}
                </Button>
                {adoption.canRevert && (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={revert.isPending && revert.variables?.body?.consumerKey === adoption.consumerKey}
                    onClick={() => revert.mutate({ body: { consumerKey: adoption.consumerKey } })}
                  >
                    <RotateCcw size={12} /> Revert
                  </Button>
                )}
              </div>
            </Can>
          </div>
        ))}
        {hiddenCount > 0 && (
          <div className="text-xs text-muted">
            {hiddenCount} more stale adoption{hiddenCount === 1 ? '' : 's'} in Catalog Review.
          </div>
        )}
      </div>
    </div>
  );
}

function formatCatalogAdoptionLabel(value: string) {
  return value.replaceAll('daemon', 'workforce').replaceAll('_', ' ');
}
